/**
 * Header bidding glue: Prebid.js → Google Publisher Tag → Google Ad Manager.
 *
 * Reads JSON config from `window.__adsConfig` (emitted by adsHeadDefault.twig):
 *   {
 *     prebidUnits:  Prebid ad-unit definitions
 *     adSlots:      [{ slotId, gptName, sizes }]
 *     targeting:    { key: value | [values] } page-level GAM targeting
 *     refreshRate:  seconds, or null for no auto-refresh
 *   }
 *
 * Defines GPT slots, runs `pbjs.requestBids()`, sets winning bid targeting on
 * GPT, calls `googletag.pubads().refresh()`. Implements auto-refresh per
 * tier and exposes `window.adsRefresh()` for SPA route changes.
 */

(function () {
    'use strict';

    // SPA compatibility shim: aliased to the (possibly no-op) window.adsRefresh
    // below. Defined unconditionally so pages without ad slots — but with the
    // SPA shell — don't throw when useLoadMapping.ts calls reloadPubstream().
    window.adsRefresh = window.adsRefresh || function () {};
    window.reloadPubstream = window.adsRefresh;

    var config = window.__adsConfig || null;
    if (!config || !config.adSlots) {
        return;
    }

    // The out-of-page interstitial is page-agnostic and must run even on pages
    // that render no in-page banner slots, so its presence — not just adSlots —
    // has to keep the initializer alive. Bail only when there is neither in-page
    // inventory nor an out-of-page format.
    var hasOutOfPage = !!(config.outOfPage && config.outOfPage.interstitial);
    if (!config.adSlots.length && !hasOutOfPage) {
        return;
    }

    // Bidder window for one auction. requestBids takes it per call and that
    // value governs; the global bidderTimeout below is set to match so a read of
    // the config reports the same number.
    var PREBID_TIMEOUT_MS = 1500;
    // Max time the TCF consent module waits for the CMP (Google Funding
    // Choices) to return a consent string. The consent gather runs BEFORE the
    // PREBID_TIMEOUT_MS bidder window, so a first-load EEA session can take up
    // to CONSENT_TIMEOUT_MS + PREBID_TIMEOUT_MS before bidsBackHandler fires.
    // Sized well above the routine first-load CMP latency so the consent
    // string is captured for the EEA/UK auction. Consumed by
    // consentManagement.gdpr.timeout below.
    var CONSENT_TIMEOUT_MS = 3000;
    // Failsafe fires only if Prebid stalls and never invokes bidsBackHandler.
    // It must cover BOTH the consent-gather phase and the bidder window: sized
    // too low, it fires mid-consent on slow-CMP EEA loads and refreshes GAM
    // with no Prebid targeting, dropping the consented demand. Sized just above
    // CONSENT_TIMEOUT_MS + PREBID_TIMEOUT_MS.
    var FAILSAFE_TIMEOUT_MS = CONSENT_TIMEOUT_MS + PREBID_TIMEOUT_MS + 200;
    // Time a slot has to report slotRenderEnded after its request is dispatched.
    // Expiry is STATE_TIMEOUT, not a no-fill: the request is still outstanding.
    var SLOT_RENDER_TIMEOUT_MS = 4000;
    // Time gpt.js has to execute; past it the tag is blocked or failed. The
    // verdict is terminal, so the budget is bounded below by
    // SLOT_RENDER_TIMEOUT_MS in AdPassbackSlotLifecycleTest.
    var GPT_BOOT_TIMEOUT_MS = 4000;

    // Per-slot outcome, dispatched on the container's [data-ad-zone] element and
    // read by ads/fallback/adFallback.twig. The names and states below are
    // pinned by tests/unit/Service/Web/Views/AdPassbackSlotLifecycleTest.php.
    var SLOT_FILLED_EVENT = 'ads:slot-filled';
    var SLOT_NOFILL_EVENT = 'ads:slot-nofill';

    // A request is expected or on the wire. The container must not touch the
    // slot's div.
    var STATE_PENDING = 'pending';
    // No request will ever be made for this slot — gpt.js never booted, or the
    // slot was never defined. Nothing is in flight, so the container is free.
    var STATE_ABSENT = 'absent';
    // GPT reported an outcome for this slot.
    var STATE_ANSWERED = 'answered';
    // A request went out and GPT never reported. The container must leave the
    // div alone; a response can still arrive.
    var STATE_TIMEOUT = 'timeout';

    var AD_NETWORK_PATH = '/1004604/';

    // Viewability gate for auto-refresh: require 50% of the slot to have
    // been within the viewport continuously for VIEWABILITY_DWELL_MS since
    // its last impression. Note: IntersectionObserver only checks viewport
    // intersection, not z-stack occlusion (a modal covering the slot still
    // counts as intersecting). For audited IAB MRC viewability we'd lean on
    // GAM Active View; this gate is a defensive proxy that matches the
    // refresh-not-billed behavior of the prior CPG bundle.
    var VIEWABILITY_THRESHOLD = 0.5;
    var VIEWABILITY_DWELL_MS = 1000;

    // Slot-level GAM targeting key carrying which refresh of that slot an
    // impression is. Absent on a slot's first request, so reporting tells
    // first-view inventory from refreshed inventory by the key's presence.
    var REFRESH_TARGETING_KEY = 'Refresh';
    // Ordinals saturate here, so the emitted set stays closed. GAM drops a
    // value outside the key's registered value list, and a dropped value is
    // indistinguishable from a first view. The top value reads as "or more".
    var REFRESH_ORDINAL_MAX = 10;
    // How often refresh eligibility is polled. config.refreshRate is enforced
    // per slot against its last dispatch, so this only bounds how late a slot
    // that has come due is picked up.
    var REFRESH_POLL_MS = 1000;

    // Bidders authorized to drop iframe-based user-sync pixels. Explicit
    // allowlist (vs '*') so adding a bidder is a deliberate decision.
    // Equativ syncs by iframe rather than image, so it only syncs if it is
    // listed here (Equativ's Prebid.js setup guide makes iframe syncs a
    // requirement of the integration).
    var USER_SYNC_BIDDERS = ['sovrn', 'ix', 'rubicon', 'equativ'];

    // Fraction of auctionEnd / bidWon events sampled (independently) and
    // reported to GA4 as header-bidding telemetry. Sampling keeps GA4 event
    // volume and quota low while still yielding a representative view of bid
    // rate, hb_pb distribution and timeout rate across traffic.
    var PREBID_TELEMETRY_SAMPLE_RATE = 0.02;

    var googletag = window.googletag = window.googletag || { cmd: [] };
    var pbjs = window.pbjs = window.pbjs || { que: [] };

    var definedSlots = [];
    var slotsByCode = {};
    var viewableState = {};   // slotId → { viewableSinceMs, lastRefreshAtMs, auctionInFlight }
    var refreshCounts = {};   // slotId → refreshes dispatched for that slot
    var slotObservers = {};   // slotId → IntersectionObserver
    var slotStates = {};      // slotId → STATE_*
    var releasedSlots = {};   // slotId → true once the passback owns the container
    var renderWatchdogs = {}; // slotId → pending watchdog handle
    // Covers GAM dispatch until the slot's outcome, where viewableState's
    // auctionInFlight covers the auction that precedes that dispatch. Kept out
    // of viewableState because that entry exists only for slots with an
    // observer, and a missing entry here would read as "safe to drop the div".
    var requestsInFlight = {};
    var refreshTimer = null;
    var initialAuctionDone = false;
    var oopSlots = [];        // out-of-page slots (interstitial)

    // Bidders that timed out, accumulated per auction id. Prebid fires the
    // bidTimeout event during the auction (before auctionEnd), so the set is
    // gathered here and read when the auction ends. Entries are removed at
    // auctionEnd so the map cannot grow unbounded.
    var prebidTimeoutsByAuction = {};

    // Warn to the console and drop a Sentry breadcrumb for a non-fatal ads
    // condition, without emitting a Sentry event per page load (same degrade
    // pattern as the setTargetingForGPTAsync path below).
    function adsWarn(message, data) {
        if (window.console && console.warn) {
            if (data) {
                console.warn('[ads-init] ' + message, data);
            } else {
                console.warn('[ads-init] ' + message);
            }
        }
        if (window.Sentry && window.Sentry.addBreadcrumb) {
            window.Sentry.addBreadcrumb({
                category: 'ads',
                level: 'warning',
                message: message,
                data: data || {}
            });
        }
    }

    // Two-decimal CPM string in the hb_pb reporting style. Non-finite,
    // negative or zero input reports as '0.00'.
    function cpmBucket(cpm) {
        var value = (typeof cpm === 'number' && isFinite(cpm) && cpm > 0) ? cpm : 0;
        return value.toFixed(2);
    }

    // Count of own enumerable keys, used for distinct bidder/timeout tallies.
    function countKeys(obj) {
        var total = 0;
        for (var key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                total += 1;
            }
        }
        return total;
    }

    // Own keys of a bidder set as a sorted comma-separated string, for GA4
    // event params (which take scalars, not arrays). Sorted so the same set of
    // bidders always produces the same value and groups in reporting.
    function bidderList(obj) {
        var names = [];
        for (var key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                names.push(key);
            }
        }
        return names.sort().join(',');
    }

    // Header-bidding telemetry: emit sampled GA4 events on auction completion
    // and on a winning bid so bid rate, hb_pb distribution and timeout rate are
    // observable without a bundle rebuild or a dedicated endpoint. gtag (GA4) is
    // loaded site-wide. Each handler is wrapped so it never throws back into
    // Prebid, and no PII is sent. Registered on the pbjs queue, so it only binds
    // when the real Prebid bundle is present (pbjs.onEvent exists).
    function installPrebidTelemetry() {
        if (typeof pbjs.onEvent !== 'function') {
            return;
        }

        pbjs.onEvent('bidTimeout', function (timedOut) {
            try {
                var entries = timedOut || [];
                for (var i = 0; i < entries.length; i++) {
                    var entry = entries[i];
                    if (!entry || !entry.auctionId || !entry.bidder) {
                        continue;
                    }
                    var bidders = prebidTimeoutsByAuction[entry.auctionId]
                        || (prebidTimeoutsByAuction[entry.auctionId] = {});
                    bidders[entry.bidder] = true;
                }
            } catch (e) { /* never break the auction */ }
        });

        pbjs.onEvent('auctionEnd', function (auction) {
            var auctionId = auction && auction.auctionId;
            var timedOutBidders = auctionId ? prebidTimeoutsByAuction[auctionId] : null;
            // Drain unconditionally (even for unsampled auctions) so the map
            // cannot leak entries.
            if (auctionId) {
                delete prebidTimeoutsByAuction[auctionId];
            }

            try {
                if (Math.random() >= PREBID_TELEMETRY_SAMPLE_RATE) {
                    return;
                }
                if (typeof window.gtag !== 'function') {
                    return;
                }

                var bidsReceived = (auction && auction.bidsReceived) || [];
                var responders = {};
                var topCpm = 0;
                for (var i = 0; i < bidsReceived.length; i++) {
                    var bid = bidsReceived[i];
                    if (!bid) {
                        continue;
                    }
                    if (bid.bidderCode) {
                        responders[bid.bidderCode] = true;
                    }
                    if (typeof bid.cpm === 'number' && bid.cpm > topCpm) {
                        topCpm = bid.cpm;
                    }
                }

                var bidderRequests = (auction && auction.bidderRequests) || [];
                var requested = {};
                for (var j = 0; j < bidderRequests.length; j++) {
                    var request = bidderRequests[j];
                    if (request && request.bidderCode) {
                        requested[request.bidderCode] = true;
                    }
                }

                var params = {
                    bidder_count: countKeys(responders),
                    bid_count: bidsReceived.length,
                    highest_cpm_bucket: cpmBucket(topCpm),
                    timeout_count: timedOutBidders ? countKeys(timedOutBidders) : 0,
                    // Which bidders were asked and which answered. bidder_count
                    // alone cannot distinguish "a bidder had nothing to bid on
                    // this impression" from "a bidder has stopped responding
                    // entirely", so a misconfigured SSP stays invisible until
                    // someone reads its dashboard. The names make the second
                    // case a GA4 query.
                    bidders_requested: bidderList(requested),
                    bidders_responded: bidderList(responders)
                };

                var start = auction && auction.timestamp;
                var end = auction && auction.auctionEnd;
                if (typeof start === 'number' && typeof end === 'number' && end >= start) {
                    params.auction_ms = end - start;
                }

                window.gtag('event', 'prebid_auction', params);
            } catch (e) { /* telemetry must never affect ad serving */ }
        });

        pbjs.onEvent('bidWon', function (bid) {
            try {
                if (Math.random() >= PREBID_TELEMETRY_SAMPLE_RATE) {
                    return;
                }
                if (typeof window.gtag !== 'function' || !bid) {
                    return;
                }
                // adUnitCode is the slot's DOM id, which adDefault.twig
                // regenerates per render, so reporting it gives GA4 one bucket
                // per impression. The GPT ad unit path is stable per placement.
                var wonSlot = slotsByCode[bid.adUnitCode];
                var wonPath = wonSlot && typeof wonSlot.getAdUnitPath === 'function'
                    ? wonSlot.getAdUnitPath()
                    : '';
                window.gtag('event', 'prebid_bid_won', {
                    bidder: String(bid.bidderCode || ''),
                    cpm_bucket: cpmBucket(typeof bid.cpm === 'number' ? bid.cpm : 0),
                    ad_unit: String(wonPath || bid.adUnitCode || '')
                });
            } catch (e) { /* telemetry must never affect ad serving */ }
        });
    }

    // Define the out-of-page GPT slot declared in config.outOfPage. It carries
    // AdX/AdSense demand only (not Prebid) and doesn't occupy in-page inventory:
    // a full-screen INTERSTITIAL, shown by Google on a qualifying navigation and
    // frequency-capped automatically. defineOutOfPageSlot returns null when the
    // format isn't supported, so the guard short-circuits cleanly. Called after
    // enableServices() — see the call site for the ordering rationale.
    function defineOutOfPageSlots() {
        var oop = config.outOfPage || null;
        if (!oop || !oop.interstitial) return;

        // GPT loaded without the out-of-page enum (partial load or an ad-blocker
        // intercepting gpt.js): the requested format can't be defined. Surface it
        // instead of silently no-opping, which reproduces the exact failure this
        // code exists to catch.
        if (!googletag.enums || !googletag.enums.OutOfPageFormat) {
            adsWarn('OutOfPageFormat enum unavailable, skipping out-of-page slots');
            return;
        }

        var iSlot = googletag.defineOutOfPageSlot(
            AD_NETWORK_PATH + oop.interstitial,
            googletag.enums.OutOfPageFormat.INTERSTITIAL
        );
        if (iSlot) {
            iSlot.addService(googletag.pubads());
            oopSlots.push(iSlot);
        }
    }

    // Slot ids still eligible for an auction: the div is on the page and the
    // passback has not taken the container over. A released container is gone
    // from the DOM, so bidding on its slot spends requests on inventory that
    // cannot render.
    function activeSlotCodes() {
        return config.adSlots
            .filter(function (slot) { return !!document.getElementById(slot.slotId); })
            .map(function (slot) { return slot.slotId; })
            .filter(function (id) { return !releasedSlots[id]; });
    }

    // Move one slot out of STATE_PENDING and tell its container. A container
    // whose deferred template never activated has no element to dispatch on;
    // the recorded state is what it reads when it activates.
    function resolveSlot(slotId, filled, state) {
        if (slotStates[slotId] !== STATE_PENDING) return;
        slotStates[slotId] = state;
        // Cleared on STATE_TIMEOUT too: holding it past an unanswered request
        // would retire the slot from refresh for the session. The div is kept by
        // the recorded STATE_TIMEOUT, which the container reads as "no passback".
        delete requestsInFlight[slotId];
        if (renderWatchdogs[slotId]) {
            clearTimeout(renderWatchdogs[slotId]);
            delete renderWatchdogs[slotId];
        }

        var domEl = document.getElementById(slotId);
        var container = domEl && domEl.closest ? domEl.closest('[data-ad-zone]') : null;
        if (!container) return;

        container.dispatchEvent(new CustomEvent(
            filled ? SLOT_FILLED_EVENT : SLOT_NOFILL_EVENT,
            { detail: { slotId: slotId, state: state } }
        ));
    }

    // `expiredState` is what expiry means at the arming site: STATE_ABSENT at
    // definition (the GAM request never went out, so a container may fall back),
    // STATE_TIMEOUT at refresh (it went out unanswered, so the div must stay).
    function armRenderWatchdog(slotId, timeoutMs, expiredState) {
        if (renderWatchdogs[slotId]) {
            clearTimeout(renderWatchdogs[slotId]);
        }
        renderWatchdogs[slotId] = setTimeout(function () {
            delete renderWatchdogs[slotId];
            resolveSlot(slotId, false, expiredState);
        }, timeoutMs);
    }

    // Pending from here rather than from slot definition, so the window covers
    // the gpt.js load as well. Until the tag boots, a container cannot tell a
    // blocked gpt.js from one that is still loading.
    config.adSlots.forEach(function (slot) {
        slotStates[slot.slotId] = STATE_PENDING;
    });

    // disableInitialLoad() means nothing is requested until this file refreshes,
    // so a slot with no watchdog and nothing on the wire has nothing coming. Gated
    // on that rather than googletag.apiReady, which only says gpt.js executed.
    setTimeout(function () {
        config.adSlots.forEach(function (slot) {
            if (renderWatchdogs[slot.slotId] || requestsInFlight[slot.slotId]) return;
            resolveSlot(slot.slotId, false, STATE_ABSENT);
        });
    }, GPT_BOOT_TIMEOUT_MS);

    // Read by ads/fallback/adFallback.twig to decide whether a request is still
    // outstanding for its container. Undefined when this file never ran, which
    // the caller reads as "no outcome is coming from here".
    window.__adsSlotState = function (slotId) {
        return slotStates[slotId] || null;
    };

    // Called by the fallback when it gives a container up. The slot's div goes
    // with it, so the slot can no longer render and must leave the refresh loop.
    window.__adsReleaseSlot = function (slotId) {
        // Set before the slot lookup below: a container can be released without
        // GPT ever having defined a slot for it, and activeSlotCodes reads only
        // this flag.
        releasedSlots[slotId] = true;
        delete slotStates[slotId];
        delete viewableState[slotId];
        delete requestsInFlight[slotId];
        if (renderWatchdogs[slotId]) {
            clearTimeout(renderWatchdogs[slotId]);
            delete renderWatchdogs[slotId];
        }
        if (slotObservers[slotId]) {
            slotObservers[slotId].disconnect();
            delete slotObservers[slotId];
        }

        var slot = slotsByCode[slotId];
        if (!slot) return;
        delete slotsByCode[slotId];

        var index = definedSlots.indexOf(slot);
        if (index !== -1) {
            definedSlots.splice(index, 1);
        }
        if (!definedSlots.length && refreshTimer) {
            clearInterval(refreshTimer);
            refreshTimer = null;
        }
        // The release can run inside GPT's own slotRenderEnded dispatch for this
        // slot, where destroySlots is re-entrant.
        setTimeout(function () {
            googletag.cmd.push(function () {
                try {
                    googletag.destroySlots([slot]);
                } catch (e) {
                    adsWarn('destroySlots failed', {
                        slotId: slotId,
                        error: e && e.message ? e.message : String(e)
                    });
                }
            });
        }, 0);
    };

    // The single definition point for both the boot pass and activateDeferred,
    // so a deferred slot gets the same lifecycle as one present at parse.
    function defineSlot(slot) {
        if (releasedSlots[slot.slotId]) return null;
        if (slotsByCode[slot.slotId]) {
            return slotsByCode[slot.slotId];
        }

        var domEl = document.getElementById(slot.slotId);
        if (!domEl) return null;

        var gptSlot = googletag
            .defineSlot(AD_NETWORK_PATH + slot.gptName, slot.sizes, slot.slotId);
        if (!gptSlot) {
            // GPT refused the unit, so no request for it will ever go out.
            releasedSlots[slot.slotId] = true;
            resolveSlot(slot.slotId, false, STATE_ABSENT);
            return null;
        }

        gptSlot.addService(googletag.pubads());
        definedSlots.push(gptSlot);
        slotsByCode[slot.slotId] = gptSlot;
        observeViewability(slot.slotId, domEl);
        // Puts the slot back in play: a template activating after the boot sweep
        // recorded STATE_ABSENT would otherwise never resolve again. A slot that
        // never reaches here has no request outstanding, so its div is droppable.
        slotStates[slot.slotId] = STATE_PENDING;
        armRenderWatchdog(slot.slotId, FAILSAFE_TIMEOUT_MS + SLOT_RENDER_TIMEOUT_MS, STATE_ABSENT);

        return gptSlot;
    }

    googletag.cmd.push(function () {
        // A slot with no div is left pending rather than given up on:
        // ads/adContent.twig can attach it later through activateDeferred, and
        // the boot sweep above is what retires one that never arrives.
        config.adSlots.forEach(function (slot) {
            defineSlot(slot);
        });

        // Registered before enableServices() so no render outcome can precede
        // the listener. Out-of-page slots report through the same event and are
        // never pending, so resolveSlot ignores them.
        googletag.pubads().addEventListener('slotRenderEnded', function (event) {
            var slot = event && event.slot;
            var slotId = slot && slot.getSlotElementId ? slot.getSlotElementId() : null;
            if (!slotId) return;
            // A slot the watchdog gave up on is re-opened here: GPT answering
            // late is still GPT answering, and its verdict outranks a timeout.
            if (slotStates[slotId] === STATE_TIMEOUT) {
                slotStates[slotId] = STATE_PENDING;
            }
            resolveSlot(slotId, !event.isEmpty, STATE_ANSWERED);
        });

        // Page-level targeting via googletag.setConfig (the legacy
        // pubads().setTargeting per-key API is deprecated as of late 2024).
        if (config.targeting && Object.keys(config.targeting).length) {
            googletag.setConfig({ targeting: config.targeting });
        }

        googletag.pubads().enableSingleRequest();
        googletag.pubads().collapseEmptyDivs();
        googletag.pubads().disableInitialLoad();
        googletag.enableServices();

        definedSlots.forEach(function (slot) {
            googletag.display(slot);
        });

        // Define the out-of-page slot after enableServices(). Defining it before
        // enableServices() does not register the interstitial on this GPT setup
        // (the slot never appears in getSlots()), whereas defining it here does.
        // It isn't part of the Prebid auction and disableInitialLoad() suppresses
        // its automatic first fetch, so display + request it once here. Google
        // handles interstitial trigger timing thereafter.
        defineOutOfPageSlots();
        // Defining these slots in the wrong phase silently drops them from pubads
        // (no error, no fill). If a slot we defined isn't registered, surface it
        // loudly instead of letting the format go dark.
        if (oopSlots.length) {
            var registeredSlots = googletag.pubads().getSlots();
            oopSlots.forEach(function (slot) {
                if (registeredSlots.indexOf(slot) === -1) {
                    adsWarn('out-of-page slot did not register', { adUnitPath: slot.getAdUnitPath() });
                }
            });
        }
        oopSlots.forEach(function (slot) {
            googletag.display(slot);
        });
        if (oopSlots.length) {
            googletag.pubads().refresh(oopSlots);
        }

        startInPageAuction();
    });

    pbjs.que.push(function () {
        pbjs.setConfig({
            currency: { adServerCurrency: 'USD' },
            // Mirrors the per-auction timeout requestBids already passes, so
            // the reported config matches the window that runs. See
            // PREBID_TIMEOUT_MS.
            bidderTimeout: PREBID_TIMEOUT_MS,
            // Penny-granular hb_pb buckets matched to the GAM "Prebid-New_*"
            // line items, which cover every $0.01 step from $0.01 to $20.00.
            // Prebid's default 'medium' granularity ($0.10 steps) floors any
            // sub-$0.10 bid to hb_pb "0.00", which matches no line item. A
            // single $0.01 bucket (precision 2) emits an hb_pb value on the
            // same ladder the line items target. Bids above $20.00 cap at the
            // top line item. This bucket is equivalent to Prebid's built-in
            // 'high' preset, written out explicitly so the match to the GAM
            // line-item ladder is self-evident.
            priceGranularity: {
                buckets: [{
                    precision: 2,
                    max: 20,
                    increment: 0.01
                }]
            },
            // TCF v2 consent. cmpApi 'iab' reads the consent string from the
            // on-page IAB __tcfapi CMP (Google Funding Choices), so EEA/UK bid
            // requests carry the TCF consent string once the CMP resolves. The
            // timeout is kept within the auction failsafe budget (see
            // CONSENT_TIMEOUT_MS / FAILSAFE_TIMEOUT_MS). defaultGdprScope is
            // false (fail open): if the CMP reports no scope, the auction
            // proceeds instead of being gated, which suits mostly-non-EEA
            // traffic. EEA/UK sessions are still gated because the CMP reports
            // gdprApplies for them. Handled by the bundled consentManagementTcf
            // module.
            consentManagement: {
                gdpr: {
                    cmpApi: 'iab',
                    timeout: CONSENT_TIMEOUT_MS,
                    defaultGdprScope: false
                }
            },
            userSync: {
                syncEnabled: true,
                filterSettings: {
                    iframe: { bidders: USER_SYNC_BIDDERS, filter: 'include' }
                },
                // First-party shared identifier (Prebid sharedIdSystem module),
                // stored in a first-party cookie, to raise bidder match rates.
                // auctionDelay lets the ID resolve before the first auction so
                // it is attached to bid requests on single-page-view sessions.
                userIds: [{
                    name: 'sharedId',
                    storage: { name: '_sharedid', type: 'cookie', expires: 365 }
                }],
                auctionDelay: 50
            },
            enableTIDs: true
        });
        if (config.prebidUnits && config.prebidUnits.length) {
            pbjs.addAdUnits(config.prebidUnits);
        }
    });

    pbjs.que.push(function () {
        installPrebidTelemetry();
    });

    // Holds a slot from the start of its auction until that auction dispatches,
    // so a poll mid-auction cannot start a second one for the same slot. A slot
    // with no viewability state yet is not held: that is the initial auction,
    // which the poll's own initialAuctionDone gate already excludes.
    function holdSlotsForAuction(adUnitCodes, held) {
        adUnitCodes.forEach(function (code) {
            var state = viewableState[code];
            if (state) {
                state.auctionInFlight = held;
            }
        });
    }

    function runAuction(adUnitCodes, options) {
        // Carried on the auction rather than applied when it starts, so a reset
        // cannot relabel an auction that is already in flight for these slots.
        var resetOrdinal = !!(options && options.resetOrdinal);
        var failsafeFired = false;
        var auctionFinished = false;

        holdSlotsForAuction(adUnitCodes, true);

        var failsafe = setTimeout(function () {
            failsafeFired = true;
            if (!auctionFinished) {
                sendBidsAndRefresh(adUnitCodes, { waitForPrebid: false, resetOrdinal: resetOrdinal });
            }
        }, FAILSAFE_TIMEOUT_MS);

        pbjs.que.push(function () {
            pbjs.requestBids({
                timeout: PREBID_TIMEOUT_MS,
                adUnitCodes: adUnitCodes,
                bidsBackHandler: function () {
                    auctionFinished = true;
                    clearTimeout(failsafe);
                    if (!failsafeFired) {
                        sendBidsAndRefresh(adUnitCodes, { resetOrdinal: resetOrdinal });
                    }
                }
            });
        });
    }

    function sendBidsAndRefresh(adUnitCodes, options) {
        var waitForPrebid = !options || options.waitForPrebid !== false;
        var resetOrdinal = !!(options && options.resetOrdinal);

        if (waitForPrebid) {
            pbjs.que.push(function () {
                refreshGptSlots(adUnitCodes, resetOrdinal);
            });
            return;
        }

        refreshGptSlots(adUnitCodes, resetOrdinal);
    }

    // Count this dispatch against each slot and, on every dispatch after a
    // slot's first, publish the running count as slot-level GAM targeting.
    // Counted per slot, not once per tick: refreshViewable() dispatches only
    // the slots that are both due and viewable, so slots on one page diverge.
    function tagRefreshOrdinal(adUnitCodes) {
        adUnitCodes.forEach(function (code) {
            var slot = slotsByCode[code];
            if (!slot) return;
            if (!Object.prototype.hasOwnProperty.call(refreshCounts, code)) {
                refreshCounts[code] = 0;
                return;
            }
            refreshCounts[code] += 1;
            // setTargeting merges per key. slot.setConfig({ targeting }) replaces
            // the slot's whole map and would drop the hb_* keys Prebid sets above.
            slot.setTargeting(
                REFRESH_TARGETING_KEY,
                String(Math.min(refreshCounts[code], REFRESH_ORDINAL_MAX))
            );
        });
    }

    function refreshGptSlots(adUnitCodes, resetOrdinal) {
        googletag.cmd.push(function () {
            try {
                dispatchToGam(adUnitCodes, resetOrdinal);
            } finally {
                // GPT swallows a throw from a command callback, so releasing
                // here is what keeps one failed dispatch from holding a slot
                // out of every later poll for the rest of the session.
                holdSlotsForAuction(adUnitCodes, false);
                initialAuctionDone = true;
            }
        });
    }

    function dispatchToGam(adUnitCodes, resetOrdinal) {
        // Gate on pbjs.libLoaded (the canonical sentinel set by the real
        // Prebid bundle) in addition to the typeof check. When an
        // ad-blocker intercepts prebid<v>.js, GPT can end up installing
        // its own pbjs shim that exposes a same-named
        // setTargetingForGPTAsync which throws on invocation. The throw
        // escapes our try/catch via GPT's internal catch-and-rethrow
        // wrapper and surfaces as a handled Sentry event. libLoaded is
        // absent on both our { que: [] } bootstrap stub and on the GPT
        // shim, so this short-circuits cleanly to the GAM refresh below.
        if (pbjs.libLoaded === true
            && typeof pbjs.setTargetingForGPTAsync === 'function') {
            try {
                pbjs.setTargetingForGPTAsync(adUnitCodes);
            } catch (e) {
                // GAM still refreshes below without bid targeting, so this
                // is a degrade-gracefully path, not an error. Breadcrumb
                // keeps the signal visible if a related issue is reported,
                // without producing a Sentry event per page load.
                if (window.console && console.warn) {
                    console.warn('[ads-init] setTargetingForGPTAsync failed', e);
                }
                if (window.Sentry && window.Sentry.addBreadcrumb) {
                    window.Sentry.addBreadcrumb({
                        category: 'ads',
                        level: 'warning',
                        message: 'setTargetingForGPTAsync failed',
                        data: { error: e && e.message }
                    });
                }
            }
        }
        if (resetOrdinal) {
            resetRefreshOrdinals(adUnitCodes);
        }
        // One slotStates entry and one watchdog per slot, so a slot already on
        // the wire is skipped rather than re-requested. The mark carries its
        // dispatch time and expires with the render window, so eligibility never
        // depends on the watchdog having fired.
        var dispatchNow = Date.now();
        var codesToRefresh = adUnitCodes.filter(function (code) {
            if (!slotsByCode[code]) return false;
            var dispatchedAtMs = requestsInFlight[code];
            return !dispatchedAtMs
                || dispatchNow - dispatchedAtMs >= SLOT_RENDER_TIMEOUT_MS;
        });
        var slotsToRefresh = codesToRefresh
            .map(function (code) { return slotsByCode[code]; });
        if (slotsToRefresh.length) {
            // Marked pending and watched before dispatch, so a slotRenderEnded
            // raised synchronously by refresh() is not clobbered back.
            codesToRefresh.forEach(function (code) {
                requestsInFlight[code] = dispatchNow;
                slotStates[code] = STATE_PENDING;
                armRenderWatchdog(code, SLOT_RENDER_TIMEOUT_MS, STATE_TIMEOUT);
            });
            // The spacing stamp is committed before both GPT calls below:
            // slot.setTargeting inside tagRefreshOrdinal, and pubads().refresh().
            // GPT swallows a throw from a command callback, and a gate left
            // unstamped is satisfied on the very next poll, so a throw in either
            // costs one interval rather than collapsing the slot's spacing to
            // the poll rate for the rest of the session.
            //
            // A refresh = new impression. Reset the dwell clock so the next
            // auto-refresh tick requires another viewable second. If the slot is
            // in-viewport, restart the clock from the dispatch time; if it left
            // the viewport, it stays at 0 until the observer fires again.
            var now = Date.now();
            codesToRefresh.forEach(function (code) {
                var state = viewableState[code];
                if (!state) return;
                // Stamped whether or not the slot is still in view, because
                // the refresh interval is measured from the impression.
                state.lastRefreshAtMs = now;
                if (state.viewableSinceMs) {
                    state.viewableSinceMs = now;
                }
            });
            tagRefreshOrdinal(codesToRefresh);
            googletag.pubads().refresh(slotsToRefresh);
        }
    }

    // A route change is a new page view for the slot, not an auto-refresh, so
    // its ordinal sequence restarts and the next request carries no key. GPT
    // keeps slot targeting until it is overwritten, so the key is cleared too.
    function resetRefreshOrdinals(adUnitCodes) {
        adUnitCodes.forEach(function (code) {
            var slot = slotsByCode[code];
            if (!slot) return;
            delete refreshCounts[code];
            slot.clearTargeting(REFRESH_TARGETING_KEY);
        });
    }

    function refreshAll() {
        if (!definedSlots.length) return;
        // Slots already mid-auction are left to it. That auction is about to
        // dispatch, and starting a second one would put two impressions on the
        // same slot milliseconds apart and label them from the dispatch order.
        var codes = activeSlotCodes()
            .filter(function (id) {
                var state = viewableState[id];
                return !state || !state.auctionInFlight;
            });
        if (!codes.length) return;
        runAuction(codes, { resetOrdinal: true });
    }

    function refreshViewable() {
        if (!definedSlots.length) return;
        var now = Date.now();
        var minIntervalMs = config.refreshRate * 1000;
        var codes = activeSlotCodes()
            .filter(function (id) {
                var s = viewableState[id];
                if (!s || s.auctionInFlight) return false;
                if (!s.viewableSinceMs) return false;
                if (now - s.viewableSinceMs < VIEWABILITY_DWELL_MS) return false;
                // Measured from the slot's last dispatch rather than from the
                // tick that started the auction, since auction latency varies
                // per auction and would otherwise pull impressions together.
                return now - s.lastRefreshAtMs >= minIntervalMs;
            });
        if (!codes.length) return;
        runAuction(codes);
    }

    function observeViewability(slotId, domEl) {
        viewableState[slotId] = { viewableSinceMs: 0, lastRefreshAtMs: 0, auctionInFlight: false };
        if (!window.IntersectionObserver) {
            // Fallback: assume viewable so we don't suppress refresh
            // entirely on browsers without IntersectionObserver.
            viewableState[slotId].viewableSinceMs = 1;
            return;
        }
        var observer = new IntersectionObserver(function (entries) {
            var state = viewableState[slotId];
            if (!state) return;
            entries.forEach(function (entry) {
                // Use intersectionRatio rather than isIntersecting — the
                // latter is just "any overlap > 0" regardless of the
                // observer threshold (Chrome quirk in some cases).
                if (entry.intersectionRatio >= VIEWABILITY_THRESHOLD) {
                    // Stamp on enter; preserve existing stamp if already in
                    // viewport (IntersectionObserver can re-fire on viewport
                    // mutations without a real exit/enter cycle).
                    if (!state.viewableSinceMs) {
                        state.viewableSinceMs = Date.now();
                    }
                } else {
                    state.viewableSinceMs = 0;
                }
            });
        }, { threshold: VIEWABILITY_THRESHOLD });
        observer.observe(domEl);
        slotObservers[slotId] = observer;
    }

    // Armed at this file's top level rather than from the registration callback:
    // the poll holds no slot state, refreshViewable() is inert until slots are
    // defined, and a cold gpt.js must not cost the page its refresh timer.
    function startAutoRefresh() {
        if (!config.refreshRate || config.refreshRate <= 0) return;
        if (refreshTimer) return;
        refreshTimer = setInterval(function () {
            if (document.hidden) return;
            if (!initialAuctionDone) return;
            refreshViewable();
        }, REFRESH_POLL_MS);
    }

    function activateDeferred(rootEl) {
        var root = rootEl && typeof rootEl.querySelectorAll === 'function' ? rootEl : document;
        var templates = [];
        var activatedSlotElements = [];

        if (typeof root.matches === 'function' && root.matches('template[data-ad-deferred]')) {
            templates.push(root);
        }
        Array.prototype.forEach.call(root.querySelectorAll('template[data-ad-deferred]'), function (template) {
            templates.push(template);
        });

        templates.forEach(function (template) {
            var content = template.content;
            var scripts = content.querySelectorAll('script');
            Array.prototype.forEach.call(content.querySelectorAll('[id]'), function (element) {
                activatedSlotElements.push(element);
            });
            template.replaceWith(content);
            scripts.forEach(function (orig) {
                var script = document.createElement('script');
                if (orig.src) {
                    script.src = orig.src;
                    script.async = true;
                } else {
                    script.textContent = orig.textContent;
                }
                orig.replaceWith(script);
            });
        });

        googletag.cmd.push(function () {
            var activatedCodes = [];
            config.adSlots.forEach(function (slot) {
                var domEl = document.getElementById(slot.slotId);
                var belongsToRoot = root === document || domEl === root || (domEl && root.contains(domEl));
                if (!domEl || (!belongsToRoot && activatedSlotElements.indexOf(domEl) === -1)) return;
                if (slotsByCode[slot.slotId]) return;

                var gptSlot = defineSlot(slot);
                if (!gptSlot) return;

                googletag.display(gptSlot);
                activatedCodes.push(slot.slotId);
            });

            if (activatedCodes.length) {
                runAuction(activatedCodes);
            }
        });
    }

    // Called from the end of the registration callback, never at this file's top
    // level: on a cold gpt.js activeSlotCodes() has not yet dropped the zones
    // whose div never rendered.
    //
    // Only runs when there is in-page banner inventory. On out-of-page-only pages
    // there are no banner slots to bid on, and requestBids with an empty
    // adUnitCodes list would be a malformed auction.
    function startInPageAuction() {
        var initialCodes = activeSlotCodes();
        if (!initialCodes.length) return;

        runAuction(initialCodes);
    }

    // Armed from configured inventory, not from the slots present right now: a
    // page whose slots are all deferred has none at this point, and the poll has
    // to be running by the time they attach. refreshViewable() re-filters each
    // tick, so arming it early dispatches nothing.
    if (config.adSlots.length) {
        startAutoRefresh();
    }

    // SPA navigation hook — re-run the auction for currently-defined slots.
    // Replaces the no-op shim defined at the top of this IIFE.
    window.adsRefresh = function () {
        if (!initialAuctionDone) return;
        refreshAll();
    };
    window.reloadPubstream = window.adsRefresh;

    googletag.cmd.push(function () {
        var queuedActivations = (window.__adsActivateDeferred && window.__adsActivateDeferred.queue) || [];
        window.__adsActivateDeferred = activateDeferred;
        queuedActivations.forEach(function (rootEl) {
            window.__adsActivateDeferred(rootEl);
        });
    });
})();
